import { useEffect, useState } from "react";
import { Check, Copy, ExternalLink, LifeBuoy, TriangleAlert } from "lucide-react";
import {
  escalationDueAt,
  escalationKindInfo,
  escalationSlaLabel,
  escalationSlaState,
  escalationStatusLabel,
  isEscalationLive
} from "../lib/kycEscalation";
import { useTimeZone } from "../lib/TimeZoneContext";
import { formatTimestamp } from "../lib/timeZone";
import type { KycEscalation } from "../types";

interface PanelProps {
  escalation: KycEscalation | null;
  /** False when the customer is verified, or an escalation is already live. */
  canOpen: boolean;
  /** True when the state suggests no ticket and only an asserted duplicate applies. */
  onlyDuplicate: boolean;
  canAct: boolean;
  onOpen: () => void;
  onUpdate: () => void;
}

const SLA_TONE: Readonly<Record<ReturnType<typeof escalationSlaState>, string>> = {
  unfiled: "warn",
  on_track: "neutral",
  due_soon: "warn",
  breached: "danger",
  closed: "neutral"
};

/**
 * The provider's side of a blocked case. Kept separate from the internal case because
 * they answer different questions: the case says what we are doing, this says what the
 * provider owes us and when. A declined or frozen customer moves through here or not
 * at all — no control in this portal can overturn a provider decision.
 */
export function KycEscalationPanel({ escalation, canOpen, onlyDuplicate, canAct, onOpen, onUpdate }: PanelProps) {
  const { timeZone } = useTimeZone();
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const timer = window.setTimeout(() => setCopied(false), 2000);
    return () => window.clearTimeout(timer);
  }, [copied]);

  if (!escalation) {
    return <section className="detail-panel-section">
      <div className="detail-panel-heading"><h3>Provider escalation</h3></div>
      <p className="detail-panel-note">
        Compliance cases go to the provider's Zendesk first; Slack is for operational problems and for an
        imminent SLA breach. Their compliance SLA is two calendar days, weekends included.
      </p>
      <button className="secondary-button full-button" onClick={onOpen} disabled={!canAct || !canOpen}>
        <LifeBuoy size={16} />Escalate to provider
      </button>
      {!canOpen
        ? <p className="detail-panel-note subtle">This customer is verified. There is nothing to escalate.</p>
        : onlyDuplicate
          ? <p className="detail-panel-note subtle">Nothing about this state calls for a ticket — the customer can still act in the app, and a review only becomes escalatable once it has passed the two-day SLA. Open one only for a duplicate you have identified.</p>
          : null}
    </section>;
  }

  const info = escalationKindInfo(escalation.kind);
  const sla = escalationSlaState(escalation);
  const due = escalation.openedAt ? escalationDueAt(escalation.openedAt) : null;
  const live = isEscalationLive(escalation);

  return <section className="detail-panel-section">
    <div className="detail-panel-heading">
      <h3>Provider escalation</h3>
      <span className={`status-badge stage-${SLA_TONE[sla] === "danger" ? "danger" : SLA_TONE[sla] === "warn" ? "warn" : "good"}`}>{escalationSlaLabel(sla)}</span>
    </div>

    {sla === "unfiled" ? <div className="escalation-alert">
      <TriangleAlert size={18} aria-hidden="true" />
      {/* Left implicit, this is how a case sits for a week while everyone assumes the
          provider has it. The provider's clock has not started. */}
      <p>This escalation has not reached the provider. File it and record the ticket reference — until then no SLA is running.</p>
    </div> : null}
    {sla === "breached" ? <div className="escalation-alert">
      <TriangleAlert size={18} aria-hidden="true" />
      <p>Past the two-day SLA. This is the point at which a Slack nudge is appropriate. Escalate sparingly — the provider works one queue across all partners.</p>
    </div> : null}

    <dl className="detail-list">
      <div><dt>Asking for</dt><dd>{info.label}</dd></div>
      <div><dt>Status</dt><dd>{escalationStatusLabel(escalation.status)}<span className="cell-subtitle">{escalation.channel === "api" ? "Filed automatically" : "Filed by an operator"}</span></dd></div>
      <div><dt>Ticket reference</dt><dd>{escalation.reference
        ? escalation.referenceUrl
          ? <a href={escalation.referenceUrl} target="_blank" rel="noreferrer noopener">{escalation.reference}<ExternalLink size={13} /></a>
          : escalation.reference
        : "Not recorded"}</dd></div>
      <div><dt>Opened</dt><dd>{formatTimestamp(escalation.openedAt, timeZone)}<span className="cell-subtitle">{escalation.openedBy ?? "—"}</span></dd></div>
      {live ? <div><dt>SLA due</dt><dd>{due ? formatTimestamp(due, timeZone) : "—"}</dd></div> : null}
      {escalation.closedAt ? <div><dt>Closed</dt><dd>{formatTimestamp(escalation.closedAt, timeZone)}</dd></div> : null}
      {escalation.outcomeNote ? <div><dt>Provider decision</dt><dd>{escalation.outcomeNote}</dd></div> : null}
    </dl>

    <p className="detail-panel-note subtle">{info.likelihood}</p>

    {escalation.body ? <>
      <div className="escalation-packet-heading">
        <span className="section-label">FILED TEXT</span>
        <button
          type="button"
          className={copied ? "copy-button copied" : "copy-button"}
          onClick={() => void navigator.clipboard.writeText(`${escalation.subject}\n\n${escalation.body}`).then(() => setCopied(true), () => setCopied(false))}
          aria-label="Copy the escalation packet"
        >{copied ? <Check size={13} /> : <Copy size={13} />}<span>{copied ? "Copied" : "Copy"}</span></button>
      </div>
      <pre className="escalation-packet">{escalation.subject}{"\n\n"}{escalation.body}</pre>
    </> : null}

    <div className="detail-panel-actions">
      <button className="secondary-button full-button" onClick={onUpdate} disabled={!canAct}>
        <LifeBuoy size={16} />{live ? "Record reference or outcome" : "Reopen or amend"}
      </button>
      {!live && canOpen ? <button className="secondary-button full-button" onClick={onOpen} disabled={!canAct}>
        <LifeBuoy size={16} />Open a new escalation
      </button> : null}
    </div>
  </section>;
}
